/**
 * The audio-reactive layer stack: the room's standing picture, and what the music pushes on top of it.
 *
 * Audio reactive stopped being one effect slot. It is two zones now — a BASELINE zone holding AT MOST ONE
 * layer, which is the room's standing picture, and a REACTIVE zone holding any number of layers, each
 * driven by what the music is doing. The baseline layer is replaced in place rather than removed and
 * re-added, so its id survives a swap; it is emptied through `{baseline: null}`, which maps to a remove at its id and is an ordinary
 * reversible operation and not a destructive one.
 *
 * A layer runs an installed effect package or one of the room's built-in audio reactions, and they are
 * the same kind of thing here — the collapse of 2026-09-18, owner: *"i think everything should be either
 * a baseline or responsive choice."*
 *
 * The zone a layer runs in is fixed for the life of the layer, and the wire word for the second zone is
 * `reactive` — which is now also the word the operator reads, owner 2026-09-18: *"let's do reactive
 * instead of responsive."*
 *
 * Everything here is pure. A caller names a new layer before dispatch, so a hold can address that same
 * id from the first frame through the controller's report.
 */

import {
  effectPackageDefaults,
  MUSIC_RATE_PARAMETER,
  type EffectPackageControlValue,
  type EffectPackageItem,
} from './effectPackages';

export const EFFECT_STACK_ZONES = ['baseline', 'reactive'] as const;
export type EffectStackZone = (typeof EFFECT_STACK_ZONES)[number];

/**
 * How the whole stack combines: `max` — brightest wins — is the default, and `add` sums instead.
 *
 * It is ONE setting on the audio-reactive effect, not a property of a layer. Owner ruling 2026-09-17:
 * *"brightest wins vs adds is a setting for the whole audio reactivity effect, it's not per sub effect."*
 */
export const EFFECT_STACK_MIXES = ['max', 'add'] as const;
export type EffectStackMix = (typeof EFFECT_STACK_MIXES)[number];

/** Every driver the controller accepts, so a stack authored elsewhere still parses. */
export const EFFECT_STACK_DRIVER_SOURCES = [
  'beat', 'downbeat', 'hit', 'energy', 'level', 'flux', 'kick', 'low', 'mid', 'high',
] as const;
export type EffectStackDriverSource = (typeof EFFECT_STACK_DRIVER_SOURCES)[number];

/** The three this console offers. A layer already carrying another driver keeps it; nothing here drops it. */
export const CONSOLE_DRIVER_SOURCES = ['beat', 'hit', 'energy'] as const;
export type ConsoleDriverSource = (typeof CONSOLE_DRIVER_SOURCES)[number];

export interface EffectStackDriver {
  source: EffectStackDriverSource;
  threshold: number;
  amount: number;
  attack: number;
  release: number;
  /** What the envelope moves. Every driver this console offers moves the layer's own output. */
  target: string;
}

export interface EffectStackLayer {
  id: string;
  /**
   * The installed package this layer runs (`nuance.` / `custom.`).
   */
  effectId: string;
  zone: EffectStackZone;
  parameters: Record<string, EffectPackageControlValue>;
  sync: { enabled: boolean; musicRate: string };
  /** Reactive layers only. A baseline layer has nothing pushing it. */
  driver?: EffectStackDriver;
}

/**
 * The room's whole audio-reactive stack: how it combines, and what is in it.
 *
 * `mix` sits beside `layers` because that is where the daemon holds it — `AudioStackParameters.mix` is a
 * sibling of `AudioStackParameters.layers`, and the published mirror mirrors that shape.
 */
export interface EffectStack {
  mix: EffectStackMix;
  layers: EffectStackLayer[];
}

/**
 * Everything but `id` is optional: a patch carries only what changed.
 *
 * `effectId` replaces the layer's PACKAGE where it stands — the layer keeps its id and its position, and
 * its parameters open on the new package's own defaults. There is no `zone`: a layer cannot change zone.
 */
export interface EffectStackLayerPatch {
  id: string;
  effectId?: string;
  parameters?: Record<string, EffectPackageControlValue>;
  sync?: Partial<EffectStackLayer['sync']>;
  driver?: Partial<EffectStackDriver>;
}

/**
 * One editor operation, translated into one or more addressed settings writes at the sender.
 */
export type EffectStackPatch =
  /**
   * Set THE baseline, replace what is running in it, or — as `null` — clear it.
   *
   * Clearing is an ordinary operation and not a destructive one: the controller writes the layer's
   * settings into the room's `zone:effectId` memory on the way out, exactly as it already does when one
   * baseline displaces another, so picking the same thing back brings the operator's own settings with
   * it. That is what makes None safe to offer with no confirmation behind it.
   */
  | { baseline: { effectId: string } | null }
  /** Append a responsive layer. An add is always responsive; the baseline is never added to. */
  | { add: { effectId: string } }
  /** Drop a responsive layer. The baseline picker uses its own None operation. */
  | { remove: string }
  /** The complete RESPONSIVE id list in the new order. */
  | { order: string[]; movedId?: string }
  /** How the whole stack combines. One setting on the effect, named by no layer. */
  | { mix: EffectStackMix }
  /**
   * The WHOLE stack at once, replacing whatever the room has — the one operation a RECALL can be.
   *
   * Every other operation here is an edit an operator makes by hand, and each one is small. Putting a
   * saved look back is not: it is a baseline, a set of responsive layers, their parameters, their music
   * settings, their drivers and the mix, all at once, and expressing it as a run of small edits would be
   * a dozen separate round trips (9-18 s each on the cloud relay, measured 2026-09-17) with a room left
   * half-recalled if one of them failed. A look goes back the way it was saved — atomically — or it does
   * not go back.
   *
   * Saved ids belong to the room in which the look was authored and are stripped from the wire. The Pi
   * mints fresh ids for a whole-list replacement; ordinary Add instead carries a caller-named id.
   */
  | { replace: EffectStack }
  | { layer: EffectStackLayerPatch };

/**
 * What a reactive layer's driver looks like before the controller has said otherwise.
 *
 * It is a DISPLAY fallback: the console renders these positions for a reactive layer the controller has
 * reported without a driver, and sends only the field the operator actually moves.
 */
export const DEFAULT_STACK_DRIVER: EffectStackDriver = {
  source: 'beat',
  threshold: 0.35,
  amount: 1,
  attack: 0.02,
  release: 0.35,
  target: 'output',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finite(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function parseZone(value: unknown): EffectStackZone {
  return value === 'reactive' ? 'reactive' : 'baseline';
}

function parseMix(value: unknown): EffectStackMix {
  return value === 'add' ? 'add' : 'max';
}

function parseDriverSource(value: unknown): EffectStackDriverSource {
  return (EFFECT_STACK_DRIVER_SOURCES as readonly unknown[]).includes(value)
    ? value as EffectStackDriverSource
    : DEFAULT_STACK_DRIVER.source;
}

/**
 * A layer's control values, in every shape the widened grammar admits.
 *
 * The scalars a package declares, plus the lists and per-element maps a built-in reaction declares —
 * `string[]` for the set and sequence kinds, `Record<string, string[]>` and `Record<string, number>`
 * for the per-element ones. Anything else is dropped rather than coerced: a value whose shape the
 * grammar does not have is one no control can draw, and drawing it as something else is the lie.
 */
function parseParameters(value: unknown): Record<string, EffectPackageControlValue> {
  const parameters: Record<string, EffectPackageControlValue> = {};
  if (!isRecord(value)) return parameters;
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'number' || typeof entry === 'boolean' || typeof entry === 'string') {
      parameters[key] = entry;
      continue;
    }
    if (Array.isArray(entry)) {
      if (entry.every((item) => typeof item === 'string')) parameters[key] = entry as string[];
      continue;
    }
    if (!isRecord(entry)) continue;
    const entries = Object.entries(entry);
    if (!entries.length) continue;
    if (entries.every(([, item]) => typeof item === 'number' && Number.isFinite(item))) {
      parameters[key] = Object.fromEntries(entries) as Record<string, number>;
      continue;
    }
    if (entries.every(([, item]) => Array.isArray(item) && item.every((one) => typeof one === 'string'))) {
      parameters[key] = Object.fromEntries(entries) as Record<string, string[]>;
    }
  }
  return parameters;
}

function parseDriver(value: unknown): EffectStackDriver | undefined {
  if (!isRecord(value)) return undefined;
  return {
    source: parseDriverSource(value.source),
    threshold: finite(value.threshold, DEFAULT_STACK_DRIVER.threshold),
    amount: finite(value.amount, DEFAULT_STACK_DRIVER.amount),
    attack: finite(value.attack, DEFAULT_STACK_DRIVER.attack),
    release: finite(value.release, DEFAULT_STACK_DRIVER.release),
    target: typeof value.target === 'string' && value.target ? value.target : DEFAULT_STACK_DRIVER.target,
  };
}

function parseLayer(value: unknown): EffectStackLayer | undefined {
  if (!isRecord(value)) return undefined;
  const id = typeof value.id === 'string' ? value.id : '';
  const effectId = typeof value.effectId === 'string' ? value.effectId : '';
  if (!id || !effectId) return undefined;
  const sync = isRecord(value.sync) ? value.sync : {};
  const zone = parseZone(value.zone);
  const driver = parseDriver(value.driver);
  return {
    id,
    effectId,
    zone,
    parameters: parseParameters(value.parameters),
    sync: {
      enabled: sync.enabled === true,
      musicRate: typeof sync.musicRate === 'string' && sync.musicRate ? sync.musicRate : 'bar',
    },
    ...(driver ? { driver } : {}),
  };
}

/**
 * The stack in `{ mix, layers: [...] }` — or the layers in a bare array — dropping anything that is not
 * a layer.
 *
 * Both the controller's live status and the room's stored stack arrive in this shape, so one reader
 * serves both and the console cannot end up with two different ideas of what a stack is. A controller
 * that does not publish `mix` yet simply omits it, and `parseMix` answers brightest-wins for it, the same
 * way every other field here answers for a controller that stayed silent.
 */
export function parseEffectStack(value: unknown): EffectStack {
  const raw = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.layers) ? value.layers : [];
  const layers: EffectStackLayer[] = [];
  for (const entry of raw) {
    const layer = parseLayer(entry);
    if (layer) layers.push(layer);
  }
  return { mix: parseMix(isRecord(value) ? value.mix : undefined), layers };
}

/**
 * A new layer's opening position: the thing's own defaults, locked to the beat.
 *
 * Owner ruling 2026-09-18: *"in audio, sync to music should be default on."* The default itself is the
 * CONTROLLER's — an add carries no `sync` at all, so the room applies its own and whatever it remembers
 * for that zone and that package wins over it — and this is only the row the operator sees under their
 * hand for the beat before the room answers. It says what the room is about to do; a row opening on
 * `false` and flipping itself on a second later would be this console guessing wrong out loud.
 */
export function effectStackLayer(
  item: EffectPackageItem,
  zone: EffectStackZone,
  id: string,
): EffectStackLayer {
  const parameters = effectPackageDefaults(item);
  const rate = parameters[MUSIC_RATE_PARAMETER];
  return {
    id,
    effectId: item.packageId,
    zone,
    parameters,
    // The controller's first-use rule: the package's own `sync.defaultLocked`, else locked.
    sync: { enabled: item.sync.defaultLocked ?? true, musicRate: typeof rate === 'string' && rate ? rate : 'bar' },
    ...(zone === 'reactive' ? { driver: { ...DEFAULT_STACK_DRIVER } } : {}),
  };
}

/**
 * The stack exactly as the panel is DRAWING it — the shape a saved look records.
 *
 * `driver` is the reason this exists. A reactive layer the controller reported without one is drawn on
 * the console's display fallback (`DEFAULT_STACK_DRIVER`), so those positions are what the operator is
 * looking at when they press Save; recording the layer without a driver would save something other than
 * what was on screen, and would be refused besides — a reactive layer must carry a driver and a baseline
 * layer must not, on the controller and in the cloud's own schema alike.
 */
export function shownEffectStack(stack: EffectStack): EffectStack {
  return {
    mix: stack.mix,
    layers: stack.layers.map((layer) => (layer.zone === 'reactive'
      ? { ...layer, driver: { ...(layer.driver ?? DEFAULT_STACK_DRIVER) } }
      : { id: layer.id, effectId: layer.effectId, zone: layer.zone, parameters: layer.parameters, sync: layer.sync })),
  };
}

/** The one layer holding the room's standing picture, if the operator has chosen one yet. */
export function baselineLayer(layers: readonly EffectStackLayer[]): EffectStackLayer | undefined {
  return layers.find((layer) => layer.zone === 'baseline');
}

/** Every layer the music drives, in the order they are stacked. */
export function responsiveLayers(layers: readonly EffectStackLayer[]): EffectStackLayer[] {
  return layers.filter((layer) => layer.zone === 'reactive');
}

/**
 * The same layer running a different package: same id, same place, same music settings, and the new
 * package's own opening values — because a slider named for one package means nothing to the next.
 */
function repackagedLayer(layer: EffectStackLayer, opened: EffectStackLayer): EffectStackLayer {
  return { ...layer, effectId: opened.effectId, parameters: opened.parameters };
}

function patchedLayer(
  layer: EffectStackLayer,
  patch: EffectStackLayerPatch,
  opened?: EffectStackLayer,
): EffectStackLayer {
  const repackaged = patch.effectId && opened ? repackagedLayer(layer, opened) : layer;
  const driver = patch.driver
    ? { ...(layer.driver ?? DEFAULT_STACK_DRIVER), ...patch.driver }
    : layer.driver;
  return {
    ...repackaged,
    parameters: patch.parameters
      ? { ...repackaged.parameters, ...patch.parameters }
      : repackaged.parameters,
    sync: patch.sync ? { ...layer.sync, ...patch.sync } : layer.sync,
    ...(layer.zone === 'reactive' ? { driver: driver ?? { ...DEFAULT_STACK_DRIVER } } : {}),
  };
}

/**
 * The patch the console just sent, applied to the stack it is showing.
 *
 * The controller is the authority; this only keeps the panel moving under the operator's hand until the
 * controller's authored settings arrive and confirm it. `opened` is the local layer for the
 * package the patch names — a new responsive layer for an `add`, or the package a `baseline` /
 * `layer.effectId` swap puts in place — and the caller builds it, because only the caller knows the
 * package's own defaults.
 */
export function applyEffectStackPatch(
  stack: EffectStack,
  patch: EffectStackPatch,
  opened?: EffectStackLayer,
): EffectStack {
  const { mix, layers } = stack;
  if ('mix' in patch) return { mix: patch.mix, layers };
  // A replace says what the whole stack is, so there is nothing of the old one to merge it into.
  if ('replace' in patch) return { mix: patch.replace.mix, layers: [...patch.replace.layers] };
  if ('baseline' in patch) {
    // A clear names no package, so there is no identity for the panel to claim; what it does claim is
    // that the room paints nothing of its own, which is just as much the room's word to give. So the
    // preview is corrected by `reconcileStack` on the very next tick exactly as a replacement is, and
    // the removal here is what the DAEMON's acknowledgement means when it answers with no stack.
    if (patch.baseline === null) {
      const current = baselineLayer(layers);
      return { mix, layers: current ? layers.filter((layer) => layer.id !== current.id) : [...layers] };
    }
    if (!opened) return { mix, layers: [...layers] };
    const current = baselineLayer(layers);
    if (!current) return { mix, layers: [{ ...opened, zone: 'baseline' }, ...layers] };
    return {
      mix,
      layers: layers.map((layer) => (layer.id === current.id ? repackagedLayer(layer, opened) : layer)),
    };
  }
  if ('add' in patch) return { mix, layers: opened ? [...layers, opened] : [...layers] };
  if ('remove' in patch) {
    // The baseline is emptied through `{baseline: null}` and never through a Remove: it is ONE slot with
    // a picker, not a list with rows, and the two gestures mean different things to the controller —
    // a clear remembers the layer's settings against its zone, so picking it back restores them.
    if (baselineLayer(layers)?.id === patch.remove) return { mix, layers: [...layers] };
    return { mix, layers: layers.filter((layer) => layer.id !== patch.remove) };
  }
  if ('order' in patch) {
    const byId = new Map(responsiveLayers(layers).map((layer) => [layer.id, layer]));
    const ordered: EffectStackLayer[] = [];
    for (const id of patch.order) {
      const layer = byId.get(id);
      if (layer) {
        ordered.push(layer);
        byId.delete(id);
      }
    }
    // The baseline keeps index 0 whatever the responsive layers do, and a responsive id the order list
    // did not name keeps its place at the end rather than disappearing.
    const baseline = baselineLayer(layers);
    return { mix, layers: [...(baseline ? [baseline] : []), ...ordered, ...byId.values()] };
  }
  return {
    mix,
    layers: layers.map((layer) => (
      layer.id === patch.layer.id ? patchedLayer(layer, patch.layer, opened) : layer
    )),
  };
}

/**
 * The RESPONSIVE layers with one of them moved a place, as the complete id list the controller expects.
 *
 * The baseline is not in the list at all: it is one layer, it is always first, and nothing reorders it.
 */
export function reorderedStackIds(
  layers: readonly EffectStackLayer[],
  id: string,
  direction: -1 | 1,
): string[] {
  const ids = responsiveLayers(layers).map((layer) => layer.id);
  const index = ids.indexOf(id);
  const swap = index + direction;
  if (index < 0 || swap < 0 || swap >= ids.length) return ids;
  [ids[index], ids[swap]] = [ids[swap], ids[index]];
  return ids;
}
